import type { ComponentType, SVGProps } from 'react';

/**
 * Wraps a raw SVGR-generated icon component to give it a default color.
 *
 * The wrapped icon renders with `style.color = defaultColor` so the icon's
 * paths (which use `currentColor`) pick up that color when rendered alone.
 * Any parent that sets `color` via CSS or passes an inline `style.color`
 * will override the default — including `<VyIconTile color="...">`, which
 * sets `color` on its inner element.
 *
 * Why this lives in the barrel and not in the SVGR pipeline: the .tsx
 * files under `components/icons/local-icons/` are auto-generated and not
 * safe to hand-edit. The barrel (`local-icons/index.ts`) is hand-maintained,
 * so the default-color decision lives there — grep-able, explicit, and
 * survives `pnpm build-icons` re-runs.
 *
 * Returns a plain function component (not forwardRef) so the wrapped
 * type matches the raw SVGR components used elsewhere in the barrel —
 * lets `VyIcon`'s `Icons[icon]` lookup union them without ref-type drift.
 *
 * @example
 *   import LightningRaw from './Lightning';
 *   export const Lightning = withDefaultColor(LightningRaw, '#de6f18');
 */
export function withDefaultColor(
  IconRaw: ComponentType<SVGProps<SVGSVGElement>>,
  defaultColor: string,
): ComponentType<SVGProps<SVGSVGElement>> {
  const Wrapped = (props: SVGProps<SVGSVGElement>) => {
    // Spread props first, then style: lets parent CSS `color` (via the
    // cascade) override our default, and consumer-provided `style.color`
    // also wins because it's merged into our style object.
    const { style, ...rest } = props;
    return <IconRaw {...rest} style={{ color: defaultColor, ...style }} />;
  };
  Wrapped.displayName = `WithDefaultColor(${IconRaw.displayName ?? IconRaw.name ?? 'Icon'})`;
  return Wrapped;
}
